import { Injectable } from '@angular/core'
import {
  AppointmentData,
  AppointmentModel,
  Card,
  CardData,
  EligibilityBenefits,
  EstimateBalance,
  EstimateData,
  EstimateHistoryData,
  EstimateHistoryModel,
  EstimateVisit,
  LetterSnapshot,
  RestService,
  TransactionData,
  TransactionModel,
} from '@mmx/shared'
import { Observable } from 'rxjs'
import { map } from 'rxjs/operators'

export interface AppointmentLookupParams {
  patientId: string
  extend?: string
  episodeId?: string
}

export type SendBalanceMethod = 'download' | 'email' | 'sms'
export type SendBalanceWhat = 'pdf' | 'link'

export interface SendBalanceMessageParams {
  method: SendBalanceMethod
  what?: SendBalanceWhat
  to?: string
}

export interface SendBalanceMessageResult {
  balance: EstimateBalance
  snapshot: LetterSnapshot
}

export interface EstimateCalculateResult {
  estimate: EstimateData
  balance: EstimateBalance
}

export interface ApplyBalancePaymentParams {
  method: string
  amount: number
  provider: string
  paymentIntentId?: string
  paymentMethodId?: string
  paymentMethodTypes?: string[]
  fee?: number
  holdPayment?: boolean
  captureAt?: string
  reason?: string
  notes?: string
  areNotesVisibleToPatient?: boolean
  holderName?: string
  patientEmail?: string
  customFields?: Record<string, unknown>
  checkNumber?: string
}

export interface ApplyBalancePaymentResult {
  balance: EstimateBalance
  transaction: TransactionData
  applied: boolean
  reason?: 'replay'
}

export interface CreateBalancePaymentIntentParams {
  amount: number
  saveCard?: boolean
  attemptToken?: string
}

export interface BalancePaymentIntent {
  id: string
  client_secret: string
  [key: string]: unknown
}

export interface CreatePaymentPlanParams {
  months: number
  monthlyAmount: number
  /** Operator-entered first installment amount; falls back to `monthlyAmount` server-side when omitted. */
  firstPayment?: number
  paymentMethodId: string
}

export interface CreatePaymentPlanResult {
  balance: EstimateBalance
}

export interface RefundTransactionParams {
  transactionId: string
  amount: number
}

export interface RefundTransactionResult {
  transaction: TransactionModel | null
  applied: boolean
  reason?: 'already-processed'
}

@Injectable({ providedIn: 'root' })
export class EstimateBalanceApiService {
  constructor(private readonly restService: RestService) {}

  getBalance(estimateId: string): Observable<EstimateBalance> {
    return this.restService
      .get<EstimateBalance>(`/estimates/${encodeURIComponent(estimateId)}/balance`)
      .pipe(map(response => response.data))
  }

  getVisits(estimateId: string): Observable<EstimateVisit[]> {
    return this.restService
      .get<EstimateVisit[]>(`/estimates/${encodeURIComponent(estimateId)}/visits`)
      .pipe(map(response => response.data))
  }

  finalize(estimateId: string): Observable<EstimateBalance> {
    return this.restService
      .post<EstimateBalance>(`/estimates/${encodeURIComponent(estimateId)}/finalize`, {})
      .pipe(map(response => response.data))
  }

  revertToDraft(estimateId: string): Observable<EstimateBalance> {
    return this.restService
      .post<EstimateBalance>(`/estimates/${encodeURIComponent(estimateId)}/revert`, {})
      .pipe(map(response => response.data))
  }

  listForPatient(patientId: string): Observable<EstimateBalance[]> {
    return this.restService
      .get<EstimateBalance[]>(`/patients/${encodeURIComponent(patientId)}/balances`)
      .pipe(map(response => response.data))
  }

  // Nullable: a direct balance fetch can resolve to no balance (e.g. the backend
  // returns 200 with an empty body). Consumers branch on null for the empty state.
  getBalanceById(balanceId: string): Observable<EstimateBalance | null> {
    return this.restService
      .get<EstimateBalance>(`/balances/${encodeURIComponent(balanceId)}`)
      .pipe(map(response => response.data ?? null))
  }

  getEstimate(estimateId: string): Observable<EstimateData> {
    return this.restService
      .get<EstimateData>(`/estimates/${encodeURIComponent(estimateId)}`)
      .pipe(map(response => response.data))
  }

  createEstimate(payload: Partial<EstimateData>): Observable<EstimateData> {
    return this.restService
      .post<EstimateData>('/estimates', payload)
      .pipe(map(response => response.data))
  }

  updateEstimate(estimateId: string, payload: Partial<EstimateData>): Observable<EstimateData> {
    return this.restService
      .put<EstimateData>(`/estimates/${encodeURIComponent(estimateId)}`, payload)
      .pipe(map(response => response.data))
  }

  deleteEstimate(estimateId: string): Observable<void> {
    return this.restService
      .delete<void>(`/estimates/${encodeURIComponent(estimateId)}`)
      .pipe(map(() => undefined))
  }

  getAppointmentsForPatient(input: AppointmentLookupParams): Observable<AppointmentModel[]> {
    const httpParams = this.restService.convertToParams({
      patientId: input.patientId,
      extend: input.extend,
      episodeId: input.episodeId,
    })
    return this.restService
      .get<AppointmentData[]>('/appointments', httpParams)
      .pipe(map(response => (response.data ?? []).map(data => new AppointmentModel(data))))
  }

  getBenefits(eligibilityId: string): Observable<EligibilityBenefits | null> {
    return this.restService
      .get<EligibilityBenefits>(`/eligibilities/${encodeURIComponent(eligibilityId)}/benefits`)
      .pipe(map(response => response.data ?? null))
  }

  getHistory(estimateId: string): Observable<EstimateHistoryModel[]> {
    return this.restService
      .get<EstimateHistoryData[]>(`/estimates/${encodeURIComponent(estimateId)}/history`)
      .pipe(map(response => (response.data ?? []).map(item => new EstimateHistoryModel(item))))
  }

  sendBalanceMessage(estimateId: string, params: SendBalanceMessageParams): Observable<SendBalanceMessageResult> {
    return this.restService
      .post<SendBalanceMessageResult>(`/estimates/${encodeURIComponent(estimateId)}/balance/message`, params)
      .pipe(map(response => response.data))
  }

  syncAppointments(estimateId: string, appointmentIds: string[]): Observable<EstimateData> {
    return this.restService
      .post<EstimateData>(`/estimates/${encodeURIComponent(estimateId)}/appointments`, { appointmentIds })
      .pipe(map(response => response.data))
  }

  calculate(estimateId: string): Observable<EstimateCalculateResult> {
    return this.restService
      .post<EstimateCalculateResult>(`/estimates/${encodeURIComponent(estimateId)}/calculate`, {})
      .pipe(map(response => response.data))
  }

  modifyStart(estimateId: string): Observable<EstimateCalculateResult> {
    return this.restService
      .post<EstimateCalculateResult>(`/estimates/${encodeURIComponent(estimateId)}/modify-start`, {})
      .pipe(map(response => response.data))
  }

  discardModify(estimateId: string): Observable<EstimateCalculateResult> {
    return this.restService
      .post<EstimateCalculateResult>(`/estimates/${encodeURIComponent(estimateId)}/discard-modify`, {})
      .pipe(map(response => response.data))
  }

  applyPayment(balanceId: string, params: ApplyBalancePaymentParams): Observable<ApplyBalancePaymentResult> {
    return this.restService
      .post<ApplyBalancePaymentResult>(`/balances/${encodeURIComponent(balanceId)}/pay`, params)
      .pipe(map(response => response.data))
  }

  createPaymentIntent(balanceId: string, params: CreateBalancePaymentIntentParams): Observable<BalancePaymentIntent> {
    return this.restService
      .post<BalancePaymentIntent>(`/balances/${encodeURIComponent(balanceId)}/payment-intent`, params)
      .pipe(map(response => response.data))
  }

  refundTransaction(params: RefundTransactionParams): Observable<RefundTransactionResult> {
    return this.restService
      .post<TransactionData | { applied: false; reason: 'already-processed' }>(
        '/payments/charges/refund',
        { refundDetails: params },
      )
      .pipe(map((response) => {
        const data = response.data
        if (data && (data as { applied?: boolean }).applied === false) {
          return {
            transaction: null,
            applied: false,
            reason: (data as { reason?: 'already-processed' }).reason,
          }
        }
        return {
          transaction: new TransactionModel(data as TransactionData),
          applied: true,
        }
      }))
  }

  listTransactions(balanceId: string): Observable<TransactionModel[]> {
    return this.restService
      .get<TransactionData[]>(`/balances/${encodeURIComponent(balanceId)}/transactions`)
      .pipe(map(response => (response.data ?? []).map(data => new TransactionModel(data))))
  }

  listSavedCards(patientId: string): Observable<Card[]> {
    return this.restService
      .get<CardData[]>(`/payments/patients/${encodeURIComponent(patientId)}/payment-methods`)
      .pipe(map(response => (response.data ?? []).map(data => new Card(data))))
  }

  createPaymentPlan(balanceId: string, params: CreatePaymentPlanParams): Observable<EstimateBalance> {
    return this.restService
      .post<CreatePaymentPlanResult>(`/balances/${encodeURIComponent(balanceId)}/payment-plan`, params)
      .pipe(map(response => response.data.balance))
  }

  cancelPaymentPlan(balanceId: string): Observable<EstimateBalance> {
    return this.restService
      .delete<CreatePaymentPlanResult>(`/balances/${encodeURIComponent(balanceId)}/payment-plan`)
      .pipe(map(response => response.data.balance))
  }
}
